import { useEffect, useMemo, useRef, useState } from "react";
import { Save, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { OPS } from "@/constants/testIds";
import { WM_TIMEOUT_MS, wmLineErrors, wmMessage } from "@/lib/wmError";
import { clearDraft, draftKey, loadDraft, newCrid, saveDraft } from "@/lib/wmDraft";
import { DaySheetLine } from "@/components/waste-meals/DaySheetLine";
import { RecordPanel } from "@/components/waste-meals/RecordPanel";
import { hasText, lossReasons } from "@/components/waste-meals/wmUtils";

const C = OPS.wm;

/** Only the notes that were not sent with a quantity survive a save. */
const keepUnsent = (notes, sentIds) => {
    const out = {};
    for (const [id, v] of Object.entries(notes || {})) if (!sentIds.has(id) && hasText(v)) out[id] = v;
    return out;
};

/**
 * The day sheet: every line of the branch's own item list, all starting
 * empty, saved in ONE tap. «حفظ» writes only the quantities typed in this
 * submission, as new records (D14). Empty lines write nothing.
 *
 * 2026-10-16 (A1): each line may carry a note. Notes live in the draft next
 * to the quantities, and editing one mints a new client_request_id exactly
 * like a quantity or reason edit (lib/wmDraft.js). A note on a line with no
 * quantity is not sent — the line says so — and it stays on screen after the
 * save instead of silently disappearing.
 */
export function DaySheetForm({ scope, catalogue, day, branchId, date, type, onSaved }) {
    const { t, lang } = useI18n();
    const { me } = useAuth();
    const userId = me?.user?.id;

    const dkey = draftKey({ userId, branchId, date, type, kind: "sheet" });
    const [values, setValues] = useState({});
    const [reasons, setReasons] = useState({});
    const [notes, setNotes] = useState({});
    const [defaultReason, setDefaultReason] = useState("");
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);
    const [panel, setPanel] = useState(null);
    const crid = useRef(newCrid());

    const lines = useMemo(() => {
        const items = catalogue?.items || [];
        const favs = catalogue?.favourites || [];
        if (!favs.length) return items;               // favourites fallback
        const byId = new Map(items.map((i) => [i.id, i]));
        return favs.map((id) => byId.get(id)).filter(Boolean);
    }, [catalogue]);

    const chips = useMemo(() => {
        const m = {};
        for (const c of day?.chips || []) m[c.item_id] = c;
        return m;
    }, [day]);

    // «مرتجع للمستودع» is never offered as the sheet-wide default: one default
    // would turn a whole sheet into returns, excluded from waste (D2).
    const defaultChoices = useMemo(() => lossReasons(catalogue?.reasons), [catalogue]);
    const effectiveDefault = defaultChoices.some((r) => r.key === defaultReason) ? defaultReason : "";

    useEffect(() => {
        const d = loadDraft(dkey);
        if (d) {
            setValues(d.values || {});
            setReasons(d.reasons || {});
            setNotes(d.notes || {});
            setDefaultReason(d.defaultReason || "");
        } else {
            setValues({}); setReasons({}); setNotes({}); setDefaultReason("");
        }
        setErrors({});
        crid.current = newCrid();
    }, [dkey]);

    const typedCount = Object.values(values).filter((v) => v && Number(v) > 0).length;

    const persist = (nextValues, nextReasons, nextDefault, nextNotes) => {
        saveDraft(dkey, {
            values: nextValues ?? values,
            reasons: nextReasons ?? reasons,
            defaultReason: nextDefault ?? defaultReason,
            notes: nextNotes ?? notes,
        });
    };

    const clearLineError = (id) => setErrors((e) => {
        if (!e[id]) return e;
        const n = { ...e }; delete n[id]; return n;
    });

    const setQty = (id, v) => {
        setFailed(false);
        crid.current = newCrid();
        clearLineError(id);
        setValues((prev) => { const next = { ...prev, [id]: v }; persist(next); return next; });
    };
    const setReason = (id, v) => {
        setFailed(false);
        crid.current = newCrid();
        setReasons((prev) => { const next = { ...prev, [id]: v }; persist(undefined, next); return next; });
    };
    // A note is part of the payload, so editing it after a failure is a NEW
    // request (idempotency rule, lib/wmDraft.js) — never a retry of the old one.
    const setNote = (id, v) => {
        setFailed(false);
        crid.current = newCrid();
        clearLineError(id);
        setNotes((prev) => {
            const next = { ...prev, [id]: v };
            persist(undefined, undefined, undefined, next);
            return next;
        });
    };

    const save = async () => {
        if (!typedCount) { toast.error(t("wm_nothing_typed")); return; }
        setBusy(true);
        setErrors({});
        const payloadLines = lines
            .map((i) => ({
                item_id: i.id, quantity: values[i.id], reason_key: reasons[i.id] || null,
                notes: (notes[i.id] || "").trim() || null,
            }))
            .filter((l) => l.quantity && Number(l.quantity) > 0);
        try {
            const res = await api.post("/waste-meals/records/bulk", {
                branch_id: branchId, date, type,
                default_reason_key: type === "waste" ? (effectiveDefault || null) : null,
                client_request_id: crid.current,
                lines: payloadLines,
            }, { timeout: WM_TIMEOUT_MS });
            const n = res.data?.created ?? payloadLines.length;
            toast.success(`${t("wm_saved_prefix")} ${n} ${t("wm_saved_suffix")}`);
            const kept = keepUnsent(notes, new Set(payloadLines.map((l) => l.item_id)));
            setValues({}); setReasons({}); setNotes(kept); setFailed(false);
            crid.current = newCrid();
            clearDraft(dkey);
            if (Object.keys(kept).length) saveDraft(dkey, { values: {}, reasons: {}, defaultReason, notes: kept });
            onSaved && (await onSaved());
        } catch (e) {
            const lineErrs = wmLineErrors(e);
            if (Object.keys(lineErrs).length) {
                const mapped = {};
                Object.entries(lineErrs).forEach(([idx, err]) => {
                    const line = payloadLines[Number(idx)];
                    if (line) mapped[line.item_id] = err;
                });
                setErrors(mapped);
                toast.error(wmMessage(e, lang) || t("wm_offline_error"));
            } else {
                const msg = wmMessage(e, lang);
                if (msg) toast.error(msg); else setFailed(true);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid={C.sheet.form}
             className="rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3 shadow-[var(--shadow-sm)]">
            <p className="text-xs text-[color:var(--ink-muted)]">{t("wm_day_sheet_hint")}</p>
            {catalogue?.favourites_fallback ? (
                <p data-testid={C.sheet.fallbackNote} className="mt-1 text-[11px] text-[color:var(--ink-faint)]">
                    {t("wm_fallback_note")}
                </p>
            ) : null}

            {type === "waste" ? (
                <div className="mt-3">
                    <Select value={effectiveDefault}
                            onValueChange={(v) => { setDefaultReason(v); persist(undefined, undefined, v); }}>
                        <SelectTrigger data-testid={C.sheet.defaultReason} className="h-9">
                            <SelectValue placeholder={t("wm_default_reason")} />
                        </SelectTrigger>
                        <SelectContent>
                            {defaultChoices.map((r) => (
                                <SelectItem key={r.key} value={r.key}>
                                    {lang === "ar" ? r.name_ar : r.name_en}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            ) : null}

            <div className="mt-2 max-h-[52vh] overflow-y-auto scrollbar-thin">
                {lines.map((i) => (
                    <DaySheetLine
                        key={i.id} item={i} chip={chips[i.id]} value={values[i.id] || ""}
                        reason={reasons[i.id] || ""} reasons={catalogue?.reasons}
                        note={notes[i.id] || ""}
                        showReason={type === "waste"}
                        warnAbove={scope?.settings?.warn_quantity}
                        error={errors[i.id]}
                        onChange={(v) => setQty(i.id, v)}
                        onReason={(v) => setReason(i.id, v)}
                        onNote={(v) => setNote(i.id, v)}
                        onOpenPanel={() => setPanel(i)}
                    />
                ))}
            </div>

            {failed ? (
                <div data-testid={C.offlineError}
                     className="mt-3 flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:rgba(220,38,38,0.08)] px-3 py-2">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--danger)]">
                        <WifiOff className="h-3.5 w-3.5" />
                        {t("wm_offline_error")}
                    </span>
                    <Button size="sm" variant="outline" data-testid={C.retry} onClick={save} disabled={busy}>
                        {t("wm_retry")}
                    </Button>
                </div>
            ) : null}

            <div className="sticky bottom-0 mt-3 flex items-center gap-2 bg-[color:var(--bg-surface)] pt-2">
                <span data-testid={C.sheet.count} className="text-xs font-semibold text-[color:var(--ink-muted)]">
                    <bdi dir="ltr">{typedCount}</bdi> {t("wm_saved_suffix")}
                </span>
                <Button data-testid={C.sheet.save} onClick={save} disabled={busy || !typedCount}
                        className="h-11 flex-1 gap-2 text-sm font-bold">
                    <Save className="h-4 w-4" />
                    {busy ? t("wm_saving") : t("wm_save")}
                </Button>
            </div>

            <RecordPanel open={!!panel} onOpenChange={(v) => !v && setPanel(null)}
                         branchId={branchId} date={date} type={type} itemId={panel?.id}
                         branchCount={panel ? (chips[panel.id]?.count ?? 0) : 0}
                         itemName={panel ? (lang === "ar" ? panel.name_ar : panel.name_en || panel.name_ar) : ""}
                         onChanged={onSaved} />
        </div>
    );
}
